/**
 * A deliberately tiny declarative expression language.
 *
 * Template authors need conditions like "only show this block when the category
 * has more than eight items", and those conditions must be evaluated on the
 * server too. Storing JavaScript and running `eval` would hand every admin a
 * remote code execution primitive on the render server, so the DSL is data and
 * this file is the only interpreter.
 */
/** Dot-path lookup with array index support: `category.items.0.name`. */
export function readPath(scope, path) {
    const segments = path.split('.');
    let current = scope;
    for (const segment of segments) {
        if (current == null)
            return undefined;
        if (Array.isArray(current)) {
            const index = Number(segment);
            current = Number.isInteger(index) ? current[index] : undefined;
            continue;
        }
        if (typeof current !== 'object')
            return undefined;
        current = current[segment];
    }
    return current;
}
function toComparable(value) {
    if (value === null)
        return null;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (value instanceof Date)
        return value.getTime();
    return undefined;
}
function compare(left, right) {
    const a = toComparable(left);
    const b = toComparable(right);
    if (a == null || b == null)
        return undefined;
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    if (typeof a === 'boolean' || typeof b === 'boolean') {
        return Number(a) - Number(b);
    }
    return String(a).localeCompare(String(b));
}
/** Truthiness, with the JS foot-guns removed: empty arrays and objects are false. */
export function isTruthy(value) {
    if (value == null)
        return false;
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0 && !Number.isNaN(value);
    if (typeof value === 'string')
        return value.trim().length > 0;
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === 'object')
        return Object.keys(value).length > 0;
    return Boolean(value);
}
export function evaluate(expression, scope) {
    if (!expression)
        return true;
    switch (expression.op) {
        case 'const':
            return expression.value;
        case 'var':
            return readPath(scope, expression.path);
        case 'and':
            return expression.args.every((arg) => isTruthy(evaluate(arg, scope)));
        case 'or':
            return expression.args.some((arg) => isTruthy(evaluate(arg, scope)));
        case 'not':
            return !isTruthy(evaluate(expression.arg, scope));
        case 'eq':
            return (toComparable(evaluate(expression.left, scope)) ===
                toComparable(evaluate(expression.right, scope)));
        case 'ne':
            return (toComparable(evaluate(expression.left, scope)) !==
                toComparable(evaluate(expression.right, scope)));
        case 'gt': {
            const delta = compare(evaluate(expression.left, scope), evaluate(expression.right, scope));
            return delta !== undefined && delta > 0;
        }
        case 'gte': {
            const delta = compare(evaluate(expression.left, scope), evaluate(expression.right, scope));
            return delta !== undefined && delta >= 0;
        }
        case 'lt': {
            const delta = compare(evaluate(expression.left, scope), evaluate(expression.right, scope));
            return delta !== undefined && delta < 0;
        }
        case 'lte': {
            const delta = compare(evaluate(expression.left, scope), evaluate(expression.right, scope));
            return delta !== undefined && delta <= 0;
        }
        case 'between': {
            const value = evaluate(expression.value, scope);
            const lower = compare(value, evaluate(expression.min, scope));
            const upper = compare(value, evaluate(expression.max, scope));
            return lower !== undefined && upper !== undefined && lower >= 0 && upper <= 0;
        }
        case 'in': {
            const value = toComparable(evaluate(expression.value, scope));
            return expression.options.some((option) => option === value);
        }
        case 'exists':
            return readPath(scope, expression.path) != null;
        case 'empty':
            return !isTruthy(readPath(scope, expression.path));
        case 'length': {
            const value = readPath(scope, expression.path);
            if (Array.isArray(value) || typeof value === 'string')
                return value.length;
            if (value && typeof value === 'object')
                return Object.keys(value).length;
            return 0;
        }
        case 'matches': {
            const value = readPath(scope, expression.path);
            if (typeof value !== 'string')
                return false;
            try {
                // Author-supplied patterns are still untrusted input; a malformed one
                // must fail the condition rather than throw mid-render.
                return new RegExp(expression.pattern).test(value);
            }
            catch {
                return false;
            }
        }
        default:
            return false;
    }
}
export function evaluateCondition(expression, scope) {
    return isTruthy(evaluate(expression, scope));
}
/** Human-readable rendering for the constructor's rule panel. */
export function describeExpression(expression) {
    if (!expression)
        return 'always';
    switch (expression.op) {
        case 'const':
            return JSON.stringify(expression.value);
        case 'var':
            return expression.path;
        case 'and':
            return expression.args.map(describeExpression).join(' and ');
        case 'or':
            return expression.args.map((arg) => `(${describeExpression(arg)})`).join(' or ');
        case 'not':
            return `not ${describeExpression(expression.arg)}`;
        case 'between':
            return `${describeExpression(expression.value)} between ${describeExpression(expression.min)} and ${describeExpression(expression.max)}`;
        case 'in':
            return `${describeExpression(expression.value)} in [${expression.options.join(', ')}]`;
        case 'exists':
            return `${expression.path} is set`;
        case 'empty':
            return `${expression.path} is empty`;
        case 'length':
            return `count of ${expression.path}`;
        case 'matches':
            return `${expression.path} matches /${expression.pattern}/`;
        default: {
            const symbols = {
                eq: '=',
                ne: '!=',
                gt: '>',
                gte: '>=',
                lt: '<',
                lte: '<=',
            };
            const symbol = symbols[expression.op] ?? expression.op;
            return `${describeExpression(expression.left)} ${symbol} ${describeExpression(expression.right)}`;
        }
    }
}
//# sourceMappingURL=expression.js.map